import { useState } from "react"
import {
  DateFormat,
  PuxButton,
  PuxIcon,
  PuxModalContent,
  PuxSheet,
  PuxSpinner,
  PuxText,
  toaster,
} from "@phonero/pux-react"
import {
  CodewordType,
  OrderStatusId,
  PaymentMethod,
} from "@phonero/common-graphql/types"
import {
  CheckoutProductHeader,
  DataBoostTheme,
  DataPackageTheme,
  AddExtraDataPackage2Document,
  AddExtraDataPackage2Mutation,
  ExtraDataPackageSingleSubscriptionFragment,
  ExtraDataPackagesSingleSubscriptionDetailsFragment,
  ExtraDataPackagesSingleSubscriptionDocument,
  InlineError,
  LogOrderProcessParams,
  Markdown,
  ShowLessOrAll,
  useAppMutation,
  useAppQuery,
  useAppTranslations,
  useCurrentSubscriptionId,
  useFaroClient,
  useSpan,
  AddDataProductHeader,
} from "@phonero/common-ux"
import css from "./Checkout.module.scss"
import { OrderStatus } from "./OrderStatus"
import { ConfirmContinue } from "./ConfirmContinue"
import { SelectPaymentMethod } from "./SelectPaymentMethod"
import { NoAvailableProducts } from "./NoAvailableProducts"
import { getTheme } from "../../../assets/themes"
import { Maybe } from "graphql/jsutils/Maybe"
import { getCurrentAppScheme } from "../../../platform"
import { aboCheck } from "@phonero/pux-icons/icons"
import {
  paymentVariation,
  usePaymentVariation,
} from "../calculatePaymentVariation"
import { useAppRouterDP } from "../../../util/useAppRouterDP"
import { routes } from "../../../routes"
import { OTELApi } from "@grafana/faro-web-sdk"
import { uncompletedPurchases } from "../../../util/reactiveVars"

export interface CheckoutProps {
  product: Pick<
    ExtraDataPackagesSingleSubscriptionDetailsFragment &
      ExtraDataPackageSingleSubscriptionFragment,
    | "productId"
    | "theme"
    | "id"
    | "price"
    | "size"
    | "name"
    | "sizeType"
    | "type"
    | "roamingDurationDays"
    | "countries"
    | "campaignExpiryDate"
    | "campaign"
    | "vippsOnly"
    | "availableOnSubscriptionsCount"
  >
  logOrderProcess: (args: Partial<LogOrderProcessParams>) => void
  onClose: (paymentID?: string) => any
  onCancel: () => any
  onRedirected: () => any
  onOrderStatusChange?: (
    prev: OrderStatusId | undefined | null,
    next: OrderStatusId
  ) => any
}

export const Checkout = ({
  onClose,
  onCancel,
  onRedirected,
  product,
  onOrderStatusChange,
  logOrderProcess,
}: CheckoutProps) => {
  if (!product) throw new Error("Product to checkout missing")
  const faroClient = useFaroClient()
  const { t, i18n } = useAppTranslations()
  const otel = useSpan("trace", "checkout")
  const { subscriptionId } = useCurrentSubscriptionId()
  const isAbroad = window.location.hash === "data-int"
  const [payUsingVipps, setPayUsingVipps] = useState<boolean>()
  if (!product?.productId) {
    throw Error("product id is missing")
  }

  const isCampaignProduct = !!product?.theme
  const theme = isCampaignProduct ? getTheme(product.theme, product.type) : undefined
  const { goTo } = useAppRouterDP()

  const isDataPackageCampaign =
    theme?.type === CodewordType.Extradataeea ||
    theme?.type === CodewordType.Extraboost

  const dataTheme = isDataPackageCampaign
    ? (theme as DataPackageTheme)
    : undefined

  // nosemgrep: semgrep.gql-missing-error-check-mutation-deconstructed-warning
  const [addExtraDataPackage, { loading, data: orderData, error }] =
    useAppMutation(AddExtraDataPackage2Document, {
      onCompleted: (data: AddExtraDataPackage2Mutation) => {
        setShowConfirmationPage(false)
        // Redirect user automatically to payemnt-providers url
        // (there is also a fallbackLink below)
        if (data.addExtraDataPackage2?.errors?.length) {
          return
        }
        const { addExtraDataPackageResult } = data?.addExtraDataPackage2
        if (
          !addExtraDataPackageResult ||
          !addExtraDataPackageResult?.paymentURI
        ) {
          return
        }

        logOrderProcess({
          stepName: "confirm-purchase",
          attributes: {
            paymentMethod: addExtraDataPackageResult.paymentMethod,
            ...(addExtraDataPackageResult.paymentMethod ===
              PaymentMethod.Vipps && {
              productID: addExtraDataPackageResult.productID ?? "",
              orderID: addExtraDataPackageResult.orderID,
            }),
          },
        })
        if (addExtraDataPackageResult?.paymentMethod === PaymentMethod.Vipps) {
          AddOtelLog(otel, addExtraDataPackageResult)

          const result = data?.addExtraDataPackage2?.addExtraDataPackageResult
          if (!result) return

          // Store the purchase-details into local storage
          uncompletedPurchases.add({
            ...result,
            productID: result.productID ?? product.productId ?? "",
            productName: product.name ?? "",
            paymentID: result.paymentID ?? "",
            source: "localStorage",
            isRedirected: false,
            productText: product.name ?? "",
            amount: product.price! * 1.25,
            date: new Date(),
            paymentURI: result.paymentURI ?? ""
          })

          // Used to avoid navigating back when closing the modal.
          onRedirected()

          // Redirect to the callback-page right away.
          // This is because there is a problem with redirecting the user from the vipps-app and into our app again
          goTo("vipps-redirect", null, {
            paymentID: result?.paymentID,
          }).paymentCallbackVipps()
        }
      },
      refetchQueries: [
        {
          query: ExtraDataPackagesSingleSubscriptionDocument,
          variables: {
            mobileSubscriptionId: subscriptionId,
          },
        },
      ],
    })
  // the api will replace this template-values
  const vippsCallback =
    getCurrentAppScheme() +
    `${routes.paymentCallbackVipps.path.slice(1)}?sessionID=${
      faroClient?.metas?.value?.session?.id || ""
    }`

  const checkout = async (method: PaymentMethod) => {
    console.log("checkout", { method })
    if (otel.current) {
      otel.current.span.addEvent("checkout", { paymentMethod: method })
    }
    if (
      product.campaign &&
      !showConfirmationPage &&
      method === PaymentMethod.Mobile
    ) {
      setShowConfirmationPage(true)
      return
    }
    if (!subscriptionId || !product.id) {
      console.error("Some required keys were missing", {
        subscriptionId,
        product,
      })
      toaster.error(t("General.UnspecifiedError"))
      return
    }

    const vipps = method === PaymentMethod.Vipps
    setPayUsingVipps(vipps)    
    await addExtraDataPackage({
      variables: {
        input: {
          input: {
            productId: String(product.productId),
            subscriptionId,
            vippsParams: vipps ? ({
              callbackUrl: vippsCallback,
              price: product.price!,
            }) : undefined,
            paymentMethod: vipps ? PaymentMethod.Vipps : PaymentMethod.Mobile,
          },
        },
      },
    })
  }

  const [showConfirmationPage, setShowConfirmationPage] =
    useState<boolean>(false)

    // If not available, show order not allowed page.
  const showNotAvailablePage = product.availableOnSubscriptionsCount === 0


  // Do we really need this query?
  const {
    data: productData,
    loading: loadingProductData,
    error: productDataError,
  } = useAppQuery(ExtraDataPackagesSingleSubscriptionDocument, {
    skip: !subscriptionId,
    variables: {
      mobileSubscriptionId: subscriptionId,
      productIds: [product.productId],
      withDetails: true,
    },
  })
  const firstProduct =
    productData?.mobileSubscription?.extraDataProducts?.[0] || product
  // If any product has vipps only set then vipps only is set for that customer
  const privateOnlyPayments = !!firstProduct && firstProduct.vippsOnly
  let { variation, error: paymentVariationError } = usePaymentVariation({
    kind: isAbroad ? "abroad" : "domestic",
    privateOnly: privateOnlyPayments,
  })

  const { addExtraDataPackageResult: result, errors } =
    orderData?.addExtraDataPackage2 ?? {
      addExtraDataPackageResult: undefined,
      errors: undefined,
    }

  return (
    <PuxModalContent>
      <AddDataProductHeader product={product} />

      <PuxSheet style={{ textAlign: "center" }} noPadding>
        {!!error?.message && (
          <div style={{ padding: "20px" }}>
            {"Bestillingen kunne ikke gjennomføres."}
          </div>
        )}
        {!!orderData && !!result?.orderID && (
          <OrderStatus
            id={result?.orderID}
            subscriptionId={subscriptionId}
            payUsingVipps={!!payUsingVipps}
            onOrderStatusChange={onOrderStatusChange}
            onClose={onClose}
          />
        )}
        <InlineError error={error} code="AddExtraDataPackage2" />
        {!!orderData?.addExtraDataPackage2.errors?.length && (
          <PuxSheet>
            {orderData?.addExtraDataPackage2.errors.some(
              (e) => e.message.indexOf("not available") > -1
            )
              ? "Produktet er under bestiling eller ikke lenger tilgjenglig. Vennligst prøv igjen senere."
              : "Produktet kan ikke bestilles akkurat nå. Vennligst prøv igjen senere."}
            <br />
          </PuxSheet>
        )}
        {!orderData && showNotAvailablePage && <NoAvailableProducts />}
        {!orderData && !showNotAvailablePage && (
          <>
            {!!result?.paymentURI && (
              <div className={css.fallbackLink}>
                {/* Link fades in with a delay in css */}
                {t("ExtraData.RedirectFallbackMessage", {
                  mdInline: false,
                  paymentMethod: payUsingVipps && PaymentMethod.Vipps,
                  url: result.paymentURI,
                })}
              </div>
            )}
            {!errors && !error?.message && (
              <>
                {!showConfirmationPage || payUsingVipps ? (
                  !!product.price ? (
                    <>
                      <InlineError
                        error={error}
                        code="SubscriptionExtraDataPackageDetails"
                      />
                      <CostSharingMessage variant={variation} />
                      {!result ? (
                        <SelectPaymentMethod
                          variant={variation}
                          loadingProductData={loadingProductData}
                          subscriptionId={subscriptionId}
                          product={product}
                          onPaymentClick={checkout}
                          disabled={loading || loadingProductData}
                        />
                      ) : null}
                      {loading && <PuxSpinner />}
                    </>
                  ) : (
                    <ConfirmContinue
                      title={
                        dataTheme?.orderButtonText ??
                        "Aktiver min " + product?.name
                      }
                      onClick={() => {
                        checkout(PaymentMethod.Mobile)
                      }}
                      onCancel={onCancel}
                      theme={theme}
                      disabled={!!error}
                    />
                  )
                ) : (
                  <>
                    {isCampaignProduct ? (
                      <ConfirmContinue
                        title={!loading ? "Bekreft" : <PuxSpinner />}
                        onClick={() => checkout(PaymentMethod.Mobile)}
                        onCancel={onCancel}
                        theme={theme}
                        disabled={!!error || loading}
                      />
                    ) : (
                      <>
                        <p>
                          <PuxButton
                            expand="block"
                            className={css.button}
                            color="primary"
                            onClick={() => checkout(PaymentMethod.Mobile)}
                            disabled={!!error || loading}
                          >
                            Kjøp
                          </PuxButton>
                        </p>
                        <p>
                          <PuxButton
                            expand="block"
                            color={"light"}
                            className={css.button}
                            onClick={() => setShowConfirmationPage(false)}
                          >
                            Avbryt
                          </PuxButton>
                        </p>
                      </>
                    )}
                  </>
                )}
              </>
            )}
            {isCampaignProduct && !!product?.campaignExpiryDate && (
              <div className={css.expiryDate}>
                <PuxText>
                  Kampanjen utløper{" "}
                  {i18n.format(product?.campaignExpiryDate, DateFormat.Date)}
                </PuxText>
              </div>
            )}
          </>
        )}
      </PuxSheet>
    </PuxModalContent>
  )
}

const CostSharingMessage = ({ variant }: { variant: paymentVariation }) => {
  const { t } = useAppTranslations()
  const tKey = "ExtraData.CostsharingMessage"
  const text = t(tKey, {
    context: String(variant),
    costLink: "/foo",
    skipMarkdown: true,
  }).trim()
  if (!text || text === tKey) {
    return null
  }
  return (
    <div className={css.iconMessage} data-cy="costMessage">
      <div>
        <PuxIcon icon={aboCheck} color="primary" size="large" />
      </div>
      <div className="typography">
        <Markdown>{text}</Markdown>
      </div>
    </div>
  )
}

function AddOtelLog(
  otel: React.MutableRefObject<
    | {
        otel: OTELApi
        span: ReturnType<ReturnType<OTELApi["trace"]["getTracer"]>["startSpan"]>
        tracer: ReturnType<OTELApi["trace"]["getTracer"]>
        context: OTELApi["context"]
      }
    | undefined
  >,
  addExtraDataPackageResult
) {
  if (otel.current) {
    otel.current.span.setAttributes({
      paymentMethod: addExtraDataPackageResult.paymentMethod,
      productID: addExtraDataPackageResult.productID,
      orderID: addExtraDataPackageResult.orderID,
    })
    otel.current.span.setAttribute(
      "paymentMethod",
      addExtraDataPackageResult.paymentMethod
    )
    otel.current.span.setAttribute(
      "paymentID",
      addExtraDataPackageResult.paymentID
    )
    otel.current.span.setAttribute("orderID", addExtraDataPackageResult.orderID)
    otel.current.span.setAttribute(
      "productID",
      addExtraDataPackageResult.productID
    )
  }
}
